import { Grid, Typography } from "@mui/material";
import { ReactComponent as RefreshCcw04Icon } from "components/Icons/refresh-ccw-04.svg";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { StatusValue } from "components/Table/StatusValue";
import { calculateCount } from "components/Table/tableHelpers";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useLocation, useNavigate } from "react-router-dom";
import { CampaignTriggers, CampaignTypes } from "types/campaigns";
import { ListingActions, ListResponse } from "types/listing";
import { AssociatedItem } from "types/segments";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { useStyles } from "../SingleSegment.styles";

export const AssociatedItems = ({
  data,
  columnName,
  listingId,
  menuLabel,
  tableName,
  lastRecalculation,
  showLastRecalculation = false,
}: {
  data?: ListResponse<AssociatedItem>;
  columnName: string;
  listingId: string;
  menuLabel: string;
  tableName: string;
  lastRecalculation?: string;
  showLastRecalculation?: boolean;
}) => {
  const { message } = useMessage();
  const classes = useStyles();
  const { listing, updateListing } = useListing(listingId);
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;

  const headItems = createHeadItems([
    {
      headerText: message({ id: "status" }),
      property: "active",
      customWidth: 140,
    },
    {
      headerText: message({ id: columnName }),
      property: "name",
    },
  ]);

  function createItems(items: AssociatedItem[]): Row[] {
    return items.map((item) => ({
      cells: [
        {
          content: <StatusValue status={item.active} />,
          property: "active",
        },
        { content: item.name, property: "name" },
      ],
      id: item.id,
      testId: `${tableName}-list-row`,
      metadata: { disabled: !item.active },
    }));
  }

  const bodyItems = data?.items ? createItems(data.items) : [];

  const getCampaignPath = (id: string) => {
    const campaignData = data?.items?.find((campaign) => campaign.id === id);
    const type = campaignData?.additionalData?.campaign_type;
    const trigger = campaignData?.additionalData?.campaign_trigger;

    switch (type) {
      case CampaignTypes.DIRECT:
        return trigger === CampaignTriggers.TIME
          ? links.editAutomation(id)
          : links.editCampaign(id);
      case CampaignTypes.REFERRAL:
        return links.editReferral(id);
      default:
        return "";
    }
  };
  const getPath = (id: string) => {
    switch (tableName) {
      case "associatedCampaigns":
        return getCampaignPath(id);
      case "associatedRewards":
        return links.editReward(id);
      case "associatedAchievements":
        return links.editAchievement(id);
      default:
        return "";
    }
  };

  return (
    <>
      <Grid item xs={12} className={classes.metricsHeader}>
        <Typography className={classes.sectionTitle}>
          {message({ id: tableName })}
        </Typography>
        {showLastRecalculation && (
          <StatusValue
            status="neutral"
            label={
              <>
                <RefreshCcw04Icon />
                {message({ id: "lastRecalculation" })}:{" "}
                {lastRecalculation ? OLDate(lastRecalculation) : "-"}
              </>
            }
          />
        )}
      </Grid>
      <Grid item xs={12} mt={8}>
        <OLTable
          disableColumnManager
          tableName={tableName}
          data-test-id={tableName}
          actions={{
            body: [
              {
                action: (
                  id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
                ) =>
                  navigate(getPath(id as string), {
                    state: {
                      prevPath: pathname,
                    },
                  }),
                label: message({ id: menuLabel }),
              },
            ],
          }}
          columns={data && headItems}
          rows={data && bodyItems}
          onSort={(prop) => {
            updateListing({
              payload: prop,
              type: ListingActions.SET_SORT,
            });
          }}
          pagination={{
            count: calculateCount(data?.total.filtered, data?.total.all),
            onChangePage: (page) =>
              updateListing({
                payload: page,
                type: ListingActions.CHANGE_PAGE,
              }),
            onChangeRowsPerPage: (rows) =>
              updateListing({
                payload: rows,
                type: ListingActions.CHANGE_ROWS_PER_PAGE,
              }),
            page: listing.page,
            rowsPerPage: listing.rowsPerPage,
          }}
        />
      </Grid>
    </>
  );
};
